import { useCallback, useEffect, useMemo } from 'react'

import { Button, DatePicker, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretUp, faEdit, faPlus, faQuestionCircle } from '@fortawesome/pro-solid-svg-icons'
import { isArray, isFunction, isNumber, isString } from 'lodash'
import moment from 'moment'
import { useSelector } from 'react-redux'

import TableFooter from './TableFooter'
import SearchField from '../../../antd/Inputs/SearchField'
import TableConfig from './TableConfig'
import { OrderDirections } from '../../../../utils/constants'
import { URIDecode, URIEncode } from '../../../../utils/apiHelper'
import { isObject } from '../../../../utils'
import TableFilters from './TableFilters'
import TableExternalActions from './TableExternalActions'
import { FA_2X } from '../../../institution/Quotas/utils/Constants'
import { ColorsPalette } from '../../../../config/colors'
import { TableParameters } from '../../../../utils/entities/tableParameters'
import Loading from '../../../shared/Loading'
import { API_DATE, getTimezoneLessMoment } from '../../../../utils/momentjs'
import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import { useLocalStorage } from '../../../../hooks/UseLocalStorage'
import DefaultExport from './DefaultExport'
import { getColumnKey } from '../utils'
import ResetButton from '../../Buttons/ResetButton'

import '../../../../assets/data-table.scss'

const ACTION_TD_STYLE = { position: 'sticky', right: '0px', zIndex: '2', display: 'flex', flexDirection: 'row', justifyContent: 'right' }

const DataTable = (
  {
    actions, columns, customLineStyle = {}, data, disabledAdd = false, display = {}, externalActions = [], filters = [], exporter,
    footerStyle = { paddingRight: '120px' }, help, loading, metadata, parameters = {}, period, storageKey, useCustomExport, getActiveFiltersCount,
    onExport, onCreate, addButtonLabel, onKeysChanged, onParametersChange, onPeriodChange, onResetFilters, onSearch
  }
) => {
  const t = useSelector(selectTranslate)

  const [tableOptions, setTableOptions] = useLocalStorage(storageKey, { tooltip: true, hiddenColumns: [] })

  const displayedColumns = useMemo(() => {
    return isArray(columns) && isArray(tableOptions.hiddenColumns) ? columns.filter(c => !tableOptions.hiddenColumns.includes(getColumnKey(c))) : []
  }, [columns, tableOptions.hiddenColumns])

  useEffect(() => {
    if (isFunction(onKeysChanged)) {
      onKeysChanged(displayedColumns.map(c => getColumnKey(c)).filter(k => k !== null))
    }
  }, [displayedColumns, onKeysChanged])

  const handlePageChange = useCallback(newPage => {
    onParametersChange(new TableParameters({ ...parameters, page: newPage }))
  }, [parameters, metadata])

  const handleSearchChange = useCallback(search => onParametersChange(new TableParameters({
    ...parameters,
    filters: {
      ...parameters.filters,
      columns: displayedColumns.map(c => getColumnKey(c)).filter(k => k !== null),
      search: URIEncode(search)
    },
    search: URIEncode(search)
  })), [parameters, displayedColumns])

  const handleOrderChange = useCallback(order => onParametersChange(new TableParameters({ ...parameters, order })), [parameters, onParametersChange])

  const handleDatesChange = useCallback(dates => onParametersChange(
    new TableParameters({ ...parameters, period: { startDate: dates[0], endDate: dates[1] } })
  ), [parameters])

  const handleDateChange = useCallback(date => {
    onParametersChange(new TableParameters({ ...parameters, period: { ...parameters.period, startDate: date.format(API_DATE) } }))
  })

  const handleConfigSelect = useCallback(({ value, type }) => {
    const newMetadata = new TableParameters({ ...parameters })

    newMetadata[type] = value
    newMetadata.page = 1

    onParametersChange(newMetadata)
  }, [parameters, metadata])

  const getValueByKey = useCallback((d, key, translate) => {
    if (typeof key === 'string') {
      return translate ? t(d[key]) : d[key]
    }

    if (typeof key === 'function') {
      return translate ? t(key(d)) : key(d)
    }

    return translate ? t('data_table.errors.invalid_key_type') : 'Invalid key type'
  }, [t])

  const ExportComponent = useMemo(() => exporter ?? DefaultExport, [exporter])

  const resetFilters = useMemo(() => {
    let filtersCount = 0
    const searchValue = parameters?.filters?.search ?? parameters.search

    if (isString(searchValue) && searchValue.trim().length !== 0) {
      filtersCount += 1
    }

    if (isFunction(getActiveFiltersCount) && isObject(parameters?.filters)) {
      const activeFilters = getActiveFiltersCount(parameters.filters)

      if (isNumber(activeFilters)) {
        filtersCount += activeFilters
      }
    }

    return filtersCount
  }, [getActiveFiltersCount, parameters?.filters, parameters.search])

  return (
    <div className='data-table'>
      <div className='data-table-header flex-row'>
        {isObject(parameters) && (isString(parameters.filters?.search ?? parameters.search)) && (
          <SearchField
            value={URIDecode(parameters.filters?.search ?? '')}
            className='assistant-reporting-search-field'
            placeholder='Search'
            onChange={handleSearchChange}
          />
        )}
        {isObject(parameters) && (
          <TableConfig
            columns={columns}
            metadata={metadata}
            parameters={tableOptions}
            onSelect={handleConfigSelect}
            onHiddenColumnsChange={hiddenColumnKeys => setTableOptions(prev => ({ ...prev, hiddenColumns: hiddenColumnKeys }))}
            onParametersChange={setTableOptions}
            columnsSelector
          />
        )}
        {(onExport || exporter) && <ExportComponent onExport={onExport} useCustomExport={useCustomExport} />}
        {filters.length > 0 && (
          <TableFilters filters={filters} parameters={parameters} onParametersChange={onParametersChange} />
        )}
        {isFunction(onResetFilters) && filters.length > 0 && (
          <ResetButton
            ghost
            disabled={!resetFilters}
            onClick={onResetFilters}
          />
        )}
        {externalActions.length > 0 && (
          <TableExternalActions actions={externalActions} />
        )}
        {help && (
          <Tooltip title={t(help)}>
            <FontAwesomeIcon icon={faQuestionCircle} size={FA_2X} color={ColorsPalette.SECOND} style={{ marginLeft: '5px' }} />
          </Tooltip>
        )}
        <div className='flex-fill' />
        {display.period && !display.date && (
          <DatePicker.RangePicker
            value={[moment(parameters.period.startDate), moment(parameters.period.endDate)]}
            onChange={handleDatesChange}
          />
        )}
        {display.date && !display.period && isObject(parameters.period) && (
          <DatePicker
            value={getTimezoneLessMoment(parameters.period.startDate)}
            onChange={handleDateChange}
          />
        )}
        {typeof onCreate === 'function' && (
          <Button type='primary' onClick={onCreate} disabled={disabledAdd}>
            <FontAwesomeIcon icon={faPlus} />
            &nbsp;
            {t(addButtonLabel || 'common.add')}
          </Button>
        )}
      </div>
      <div className='data-table-container'>
        <div className='table-container'>
          <table className='table'>
            <thead>
              <tr>
                {displayedColumns.map((c, index) => {
                  const props = { style: { ...c.style } ?? {} }
                  let isSelectedOrder = false

                  if (!props.style.cursor) {
                    props.style.cursor = 'default'
                  }

                  if (c.orderBy && parameters.order.by) {
                    isSelectedOrder = c.orderBy === parameters.order.by

                    props.style.cursor = 'pointer'

                    props.onClick = () => handleOrderChange({
                      by: c.orderBy,
                      direction: isSelectedOrder && parameters.order.direction === OrderDirections.ASC ? OrderDirections.DESC : OrderDirections.ASC
                    })
                  }

                  return (
                    <th key={'col-' + index} {...props}>
                      <div className='data-table-title'>
                        {t(c.title)}
                        {isSelectedOrder && (
                          <>
                            <div className='flex-fill' />
                            <FontAwesomeIcon
                              icon={parameters.order.direction === OrderDirections.ASC ? faCaretUp : faCaretDown}
                              size='2x'
                            />
                          </>
                        )}
                      </div>
                    </th>
                  )
                })}
                {isArray(actions) && actions.length > 0 && (
                  <th key='actions' style={{ cursor: 'default', position: 'sticky', right: '0px', zIndex: '2', background: ColorsPalette.WHITE }}>
                    {t('data_table.column_title.actions')}
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {!loading && data.map((d, rindex) => {
                return (
                  <tr key={'row-' + rindex}>
                    {displayedColumns.map((c, cindex) => {
                      let content = getValueByKey(d, c.key, c.translate)

                      if (c.tooltip && tableOptions.tooltip) {
                        const tooltipProps = {}

                        if (typeof c.tooltip === 'function') {
                          tooltipProps.title = getValueByKey(d, c.tooltip, c.translate)
                        } else if (isObject(c.tooltip)) {
                          tooltipProps.title = getValueByKey(d, c.tooltip.getter, c.translate)
                          tooltipProps.placement = c.tooltip.placement ?? 'top'
                        }

                        content = <Tooltip {...tooltipProps}> {content} </Tooltip>
                      }

                      return (
                        <td key={`cell-${rindex}.${cindex}`} style={{ ...c.style ?? {}, ...customLineStyle }}>
                          {content}
                        </td>
                      )
                    })}
                    {isArray(actions) && actions.length > 0 && (
                      <td style={{ ...ACTION_TD_STYLE, ...customLineStyle, background: rindex % 2 ? ColorsPalette.WHITE : ColorsPalette.WHITE_SMOKE }}>
                        {actions.map((a, index) => {
                          if (typeof a.toRender === 'function' && !a.toRender(d)) {
                            return null
                          } else if (typeof a.render === 'function') {
                            const props = { key: `actions-${index}` }

                            if (a.containerClassName) {
                              props.className = a.containerClassName
                            }

                            return (
                              <div {...props}> {a.render(d, index, t)} </div>
                            )
                          } else {
                            return (
                              <Tooltip key={`actions-${index}`} placement='top' title={t(a.title)}>
                                <Button onClick={() => a.onClick(d)}>
                                  <FontAwesomeIcon icon={a.icon ?? faEdit} />
                                </Button>
                              </Tooltip>
                            )
                          }
                        })}
                      </td>
                    )}
                  </tr>
                )
              })}
              {loading && (
                <tr>
                  <td colSpan={columns.length + 1} style={{ backgroundColor: ColorsPalette.WHITE }}>
                    <Loading size='2x' style={{ marginBottom: '30px' }} />
                  </td>
                </tr>
              )}
              {!loading && data.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} style={{ backgroundColor: ColorsPalette.WHITE }}>
                    <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'center', fontWeight: 'bold' }}>
                      {t('data_table.no-data-available')}
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!loading && metadata?.total > 0 && (
          <TableFooter
            className='data-table-footer'
            style={footerStyle}
            metadata={metadata}
            deprecatedPage={parameters.page}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  )
}

export default DataTable
